import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Logo } from '../components/Logo';
import PageTitle from '../components/PageTitle';
import { 
  Star,
  Award,
  Users,
  Play,
  ChevronRight,
  Calendar,
  LogOut,
  User,
  Settings,
  Gift
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const InstructorsPage = () => {
  const { api, isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Static instructor data with authentic Lagree profiles
  const staticInstructors = [
    {
      id: "inst-1",
      name: "Danielle Powers",
      title: "Senior Lagree Instructor",
      bio: "With over 15 years mastering the Lagree Method, Danielle brings unparalleled expertise to every class. Her signature approach focuses on Time Under Tension techniques and core transformation. Former dancer turned Lagree devotee, she believes in the power of slow, controlled movements to create lasting change.",
      specialties: ["Full Body Transformation", "Core Sculpting", "Time Under Tension"],
      certifications: ["Lagree Master Trainer", "CPR/AED Certified", "Anatomy Specialist"],
      image: "https://images.unsplash.com/photo-1594381898411-846e7d193883?q=85&w=600&auto=format&fit=crop",
      class_count: 2500,
      student_count: 12000,
      rating: 4.9,
      featured: true
    },
    {
      id: "inst-2",
      name: "Michelle Rodriguez",
      title: "Lagree Master Trainer",
      bio: "Michelle's dance background brings grace and precision to her Lagree classes. She specializes in helping beginners master the Megaformer fundamentals while challenging advanced practitioners with complex movement sequences. Her classes feature signature exercises like the Escalator Lunge and Spider Kick.",
      specialties: ["Beginner Foundations", "Lower Body Focus", "Flexibility"],
      certifications: ["Lagree Certified Level 3", "Movement Specialist", "Pilates Mat"],
      image: "https://images.unsplash.com/photo-1574230951449-2635f5cb3bc6?q=85&w=600&auto=format&fit=crop",
      class_count: 1800,
      student_count: 8500,
      rating: 4.8,
      featured: true
    },
    {
      id: "inst-3",
      name: "Amanda Chen",
      title: "Elite Lagree Coach",
      bio: "Amanda's expertise in effective resistance training makes her classes uniquely challenging. She focuses on the mind-body connection and proper spring load selection for optimal muscle activation. Known for her detailed cueing on exercises like Scrambled Eggs and Wheelbarrow.",
      specialties: ["HIIT Integration", "Upper Body Definition", "Spring Resistance"],
      certifications: ["Lagree Certified", "NASM Personal Training", "Corrective Exercise"],
      image: "https://images.unsplash.com/photo-1609505848912-b7c3b8b4beda?q=85&w=600&auto=format&fit=crop",
      class_count: 1200,
      student_count: 5600,
      rating: 4.7,
      featured: true
    }
  ];

  useEffect(() => {
    // Use static data but could fetch from API
    setInstructors(staticInstructors);
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#090D0F] flex items-center justify-center">
        <div className="animate-pulse text-zinc-500">Loading instructors...</div>
      </div>
    );
  }

  const featuredInstructors = instructors.filter(i => i.featured);
  const allInstructors = instructors.filter(i => !i.featured);

  return (
    <div className="min-h-screen bg-[#090D0F]">
      <PageTitle title="Our Instructors" description="Meet our certified Lagree instructors - experts in the Lagree Method." />
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo linkTo={isAuthenticated ? "/dashboard" : "/"} size="default" />
          <nav className="hidden md:flex items-center gap-6">
            {isAuthenticated ? (
              <>
                <Link to="/dashboard" className="text-zinc-400 hover:text-white transition-colors">Home</Link>
                <Link to="/classes" className="text-zinc-400 hover:text-white transition-colors">Classes</Link>
                <Link to="/programs" className="text-zinc-400 hover:text-white transition-colors">Programs</Link>
                <Link to="/shop" className="text-zinc-400 hover:text-white transition-colors">Shop</Link>
                <Link to="/instructors" className="text-white font-medium">Instructors</Link>
              </>
            ) : (
              <>
                <Link to="/shop" className="text-zinc-400 hover:text-white transition-colors">Shop Machines</Link>
                <Link to="/the-method" className="text-zinc-400 hover:text-white transition-colors">The Method</Link>
                <Link to="/instructors" className="text-white font-medium">Instructors</Link>
              </>
            )}
          </nav>
          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                  <div className="w-8 h-8 bg-teal-400 rounded-full flex items-center justify-center text-sm font-bold text-black">
                    {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                  </div>
                  <span className="hidden md:inline text-zinc-300">{user?.first_name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-[#121212] border-zinc-800">
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
                    <User className="w-4 h-4" />
                    Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/referrals" className="flex items-center gap-2 cursor-pointer text-teal-400">
                    <Gift className="w-4 h-4" />
                    Refer & Earn $50
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                    <Settings className="w-4 h-4" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="flex items-center gap-2 text-red-400 cursor-pointer"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login">
              <Button className="bg-teal-400 text-black font-bold uppercase tracking-wider hover:bg-teal-300">
                Sign In
              </Button>
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-16">
          <span className="text-teal-400 text-sm font-bold uppercase tracking-wider mb-4 block">Your Coaches</span>
          <h1 className="text-4xl md:text-6xl font-bold mb-4">CERTIFIED LAGREE INSTRUCTORS</h1>
          <p className="text-zinc-500 text-lg max-w-2xl mx-auto">
            Learn from elite trainers certified in the authentic Lagree Method—not Pilates instructors, but dedicated Lagree professionals who understand Time Under Tension and the Megaformer.
          </p>
        </div>

        {/* Featured Instructors */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-8">FEATURED INSTRUCTORS</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {featuredInstructors.map((instructor) => (
              <div 
                key={instructor.id}
                className="bg-[#121416] border border-zinc-800 rounded-sm overflow-hidden hover:border-teal-400/30 transition-colors"
              >
                <div className="flex flex-col md:flex-row">
                  <div className="md:w-48 h-48 md:h-auto overflow-hidden flex-shrink-0">
                    <img 
                      src={instructor.image}
                      alt={instructor.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6 flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Award className="w-4 h-4 text-teal-400" />
                      <span className="text-teal-400 text-xs font-medium uppercase tracking-wider">{instructor.title}</span>
                    </div>
                    <h3 className="text-xl font-bold mb-2">{instructor.name}</h3>
                    <p className="text-zinc-400 text-sm mb-4 line-clamp-3">{instructor.bio}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {instructor.specialties.slice(0, 3).map((spec, i) => (
                        <span key={i} className="px-2 py-1 bg-zinc-800 text-zinc-400 text-xs rounded-sm">
                          {spec}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          <span>{instructor.rating}</span>
                        </div>
                        <div className="flex items-center gap-1 text-zinc-500">
                          <Play className="w-4 h-4" />
                          <span>{instructor.class_count.toLocaleString()} classes</span>
                        </div>
                      </div>
                      <Link to={`/classes?instructor=${instructor.id}`} className="text-teal-400 hover:text-teal-300 text-sm font-medium">
                        View Classes →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* All Instructors */}
        <section>
          <h2 className="text-2xl font-bold mb-8">ALL INSTRUCTORS</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allInstructors.map((instructor) => (
              <div 
                key={instructor.id}
                className="bg-[#121416] border border-zinc-800 rounded-sm overflow-hidden hover:border-teal-400/30 transition-colors group"
              >
                <div className="aspect-[4/3] relative overflow-hidden">
                  <img 
                    src={instructor.image}
                    alt={instructor.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#121416] via-transparent to-transparent" />
                </div>
                <div className="p-6 -mt-16 relative z-10">
                  <div className="flex items-center gap-2 mb-2">
                    <Award className="w-4 h-4 text-teal-400" />
                    <span className="text-teal-400 text-xs font-medium uppercase tracking-wider">{instructor.title}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-2">{instructor.name}</h3>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {instructor.specialties.slice(0, 2).map((spec, i) => (
                      <span key={i} className="px-2 py-1 bg-zinc-800 text-zinc-400 text-xs rounded-sm">
                        {spec}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
                    <div className="flex items-center gap-2 text-sm">
                      <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                      <span>{instructor.rating}</span>
                      <span className="text-zinc-600">•</span>
                      <span className="text-zinc-500">{instructor.class_count.toLocaleString()} classes</span>
                    </div>
                  </div>
                  
                  <Link to={`/classes?instructor=${instructor.id}`}>
                    <Button variant="outline" className="w-full mt-4 border-zinc-700 hover:bg-zinc-800 text-sm">
                      View Classes
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-16 bg-teal-400/10 border border-teal-400/30 rounded-sm p-12 text-center">
          <h3 className="text-2xl font-bold mb-4">Ready to Train with the Best?</h3>
          <p className="text-zinc-400 mb-6 max-w-xl mx-auto">
            Start your 7-day free trial and experience classes from our certified Lagree instructors.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button className="bg-teal-400 text-black font-bold uppercase hover:bg-teal-300">
                Start Free Trial
              </Button>
            </Link>
            <Link to="/classes">
              <Button variant="outline" className="border-zinc-700 hover:bg-zinc-800">
                Browse Classes
              </Button>
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-zinc-800 mt-16">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <Logo linkTo="/" size="small" />
          <div className="text-zinc-600 text-sm">
            © 2026 Lagree MVMT. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default InstructorsPage;
